import React, { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';
import { withStyles, Grid, IconButton, Typography, Tooltip, FormControlLabel, Select, MenuItem, Divider, OutlinedInput } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import format from 'date-fns/format';
import ComboBoxSearch from '../ComboBoxSearch';
import coachCapIcon from '../../assets/baseball-cap-blue.png';
import umpMaskIcon from '../../assets/umpire-mask.png';
import umpCapIcon from '../../assets/umpire-cap.png';
import cancelledGameIcon from '../../assets/cancelled-game.png';
import cheerIcon from '../../assets/cheer.png';

const styles = {
  card: {
    width: '100%',
    paddingRight: 4
  },
  form: {
    margin: 0,
  },
  divider: {
    marginTop: 8
  },
  selectLabel: {
    marginRight: 8
  },
  select: {
    padding: 8,
    minWidth: 30,
  },
  icon: {
    marginLeft: 10,
    verticalAlign: 'middle'
  }
};

class GameCard extends PureComponent {
    state = {
        selectedUmp: {},
        error: ''
      };

  handleRemoveUmp = (umpireId, gameId, position) => {
      this.props.removeUmp(umpireId, gameId, position);
    };

  handleSelectUmp = (selectedOption, gameId, position) => {
    if (selectedOption) {
      const { value, label } = selectedOption;
      this.setState((prevState, props) => ({
        selectedUmp: {
          [position]: {
            name: label,
            umpireId: value,
            gameId,
            position
          }
        }
      }));
    } else {
      this.setState({ selectedUmp: { [position]: null } });
    }
  };

  handleAssignUmp = (gameId, position) => {

    if (this.state.selectedUmp[position]) {
      const { umpireId } = this.state.selectedUmp[position];
      this.props.assignUmpToGame(umpireId, gameId, position);
    } else {
      this.setState({ error: 'Please select an umpire.' });
    }
  };

  handleUmpSignUp = (memberId, gameId, position) => {
    this.props.assignUmpToGame(memberId, gameId, position);
  };

  handleUmpsRequested = (selection, gameId) => {
    const { value } = selection.target;
    this.props.updateUmpAvailableForGame(gameId, value);
  };

  renderUmpName(umpId, umpName, umpLevel, isYouth, position) {
    const { isUmpire, umpires, umpiresList, isAdmin, gameId } = this.props;
  
    if (umpId === null || umpId === '') {
      if (isAdmin) {
        let umpiresAvailableList = [...umpiresList];

        //Remove Level 0 Umpires
        let index;
        let length = umpiresAvailableList.length;
        for (index = 0; index < length; index++) {
          if (umpiresAvailableList[index].level === "0") {
              umpiresAvailableList.splice(index,1);
              length--;
              index--;
          }
        }
        // If Plate assignment, remove Umpires that are not designated as Plate Umpires
        // Else it is a Base assignment, so remove Umpires that are not designated as Base Umpires
        if (position === 'umpPlate') {
          length = umpiresAvailableList.length;
          for (index = 0; index < length; index++) {
            if (!umpiresAvailableList[index].plateUmpire) {
                umpiresAvailableList.splice(index,1);
                length--;
                index--;
            }
          }
        } else {
          length = umpiresAvailableList.length;
          for (index = 0; index < length; index++) {
            if (!umpiresAvailableList[index].baseUmpire) {
                umpiresAvailableList.splice(index,1);
                length--;
                index--;
            }
          }
        }

        return(
          <ComboBoxSearch
            key="top"
            placeholder="Type to search umpires"
            items={umpiresAvailableList}
            onItemChange={selectedOption => this.handleSelectUmp(selectedOption, gameId, position)}
          />);
      }
      else {
        return(
          <Typography variant="body2" style={{fontStyle: 'italic'}}>
              No Umpire
          </Typography>);
      }
    } else {
      return(
        <Fragment>
          <Typography variant="subtitle1">
              {umpName} ({umpLevel}){isYouth ? ' Y' : ''}
          </Typography>
          { isUmpire && umpires[umpId] && 
            <Typography variant="subtitle2" color="textSecondary">{umpires[umpId].phoneNumber}</Typography>
          }
        </Fragment>);
    }
  }

  renderUmpButton(umpId, position) {
    const { isUmpire, isAdmin, isCoach, memberId, umpires, games, gameId, seasonDivisions } = this.props;
    const game = games[gameId];
    let isOnGame = false;
    const today = new Date();
    const finish = new Date(game.finishDate);
    const isFinished = finish < today;
    let umpire = null;
    if (isUmpire && umpires.hasOwnProperty(memberId)) {
      umpire = umpires[memberId];
    }

    if (umpId === null) {
      if (isAdmin) {
        return(
          <Tooltip id="tooltip-assign" title="Assign selected Umpire">
            <IconButton
              size="small" color="primary"
              onClick={() => this.handleAssignUmp(gameId, position)}
            >
              <AddIcon />
            </IconButton>
          </Tooltip>);
      }
      else {
        if (isUmpire && !game.cancelled) {
          //Check if Umpire is already assigned to the Game
          if (
            memberId === games[gameId].umpPlateId ||
            memberId === games[gameId].umpBase1Id ||
            memberId === games[gameId].umpBase2Id ||
            memberId === games[gameId].umpBase3Id
          ) isOnGame = true;
          
          //Get minimum Level required to Umpire the Game and check that the Umpire is at the level (or higher)
          let minLevel = 1;
          let isValidLevel = false; 
          switch(position) {
            case 'umpPlate':
              minLevel = !seasonDivisions[game.divisionId].umpPlateLevel ? 2 : seasonDivisions[game.divisionId].umpPlateLevel;
              break;
            case 'umpBase1':
              minLevel = !seasonDivisions[game.divisionId].umpBase1Level ? 1 : seasonDivisions[game.divisionId].umpBase1Level;
              break;
            case 'umpBase2':
              minLevel = !seasonDivisions[game.divisionId].umpBase2Level ? 1 : seasonDivisions[game.divisionId].umpBase2Level;
              break;
            case 'umpBase3':
              minLevel = !seasonDivisions[game.divisionId].umpBase3Level ? 1 : seasonDivisions[game.divisionId].umpBase3Level;
              break;
            default:
              break;
          }
          if (umpire && umpire.umpireLevel >= minLevel) {
            isValidLevel = true;
          }

          //Check that the Umpire is allowed to sign up at this position
          let isPositionUmpire = false;
          if (position === 'umpPlate') {
            if (umpire && umpire.plateUmpire) {
              isPositionUmpire = true;
            }
          } else {
            if (umpire && umpire.baseUmpire) {
              isPositionUmpire = true;
            }
          }
      
          //Allow the Umpire to sign up for the the game if all the conditions are okay
          if (!isOnGame && isValidLevel && isPositionUmpire && !isCoach) {
            return(
              <Tooltip id="tooltip-signup" title="Sign up">
                <IconButton
                  size="small" color="primary"
                  onClick={() => this.handleUmpSignUp(memberId, gameId, position)}
                >
                  <AddIcon />
                </IconButton>
              </Tooltip>);
          }
        }
        return;
      }
    } else {
      //Allow removal an Ump from a game if in Admin mode,
      //Or allow Ump to remove self if the game hasn't finished
      if (isAdmin || ((umpId === memberId) && !isFinished))
        return (
          <Tooltip id="tooltip-remove" title="Remove from Game">
            <IconButton
              size="small" color="secondary"
              onClick={() => this.handleRemoveUmp(umpId, gameId, position)}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>);
    }
    return;
  }

  renderUmpsRequested (game) {
    const { classes } = this.props;

    return(
      <FormControlLabel
        className={classes.form}
        label={<Typography className={classes.selectLabel} variant="body2">Umpires Requested:</Typography>}
        labelPlacement="start"
        control={
          <Select
            classes={{select: classes.select}}
            value={game.umpAvailable}
            input={
              <OutlinedInput
                labelWidth={0}
              />
            }
            onChange={(selection) => {this.handleUmpsRequested(selection, game.id)}}
          >
            <MenuItem dense value={1}>1</MenuItem>
            <MenuItem dense value={2}>2</MenuItem>
            <MenuItem dense value={3}>3</MenuItem>
            <MenuItem dense value={4}>4</MenuItem>
          </Select>}
      />
    );
  }

  render() {
    const { classes } = this.props;
    const { games, gameId, seasonDivisions, memberId, isAdmin, isCoach, isTeamVolunteer } = this.props;
    const game = games[gameId];
    let minPlateLevel, minBase1Level, minBase2Level, minBase3Level;
    let isPlateUmpire = false;
    let isBaseUmpire = false;

    const umpsRequested =
      game.umpAvailable === null || game.umpAvailable === '' ? 1 : game.umpAvailable;
    minPlateLevel =
      !seasonDivisions[game.divisionId].umpPlateLevel ? 2 : seasonDivisions[game.divisionId].umpPlateLevel;
    if (umpsRequested >= 2)
      minBase1Level =
        !seasonDivisions[game.divisionId].umpBase1Level ? 1 : seasonDivisions[game.divisionId].umpBase1Level;
    if (umpsRequested >= 3)
      minBase2Level =
        !seasonDivisions[game.divisionId].umpBase2Level ? 1 : seasonDivisions[game.divisionId].umpBase2Level;
    if (umpsRequested >= 4)
      minBase3Level =
        !seasonDivisions[game.divisionId].umpBase3Level ? 1 : seasonDivisions[game.divisionId].umpBase3Level;

    if (memberId === game.umpPlateId)
      isPlateUmpire = true;
    if (memberId === game.umpBase1Id || memberId === game.umpBase2Id || memberId === game.umpBase3Id)
      isBaseUmpire = true;

    return (
      <div className={classes.card}>
        <Grid container spacing={8} alignItems="flex-start">
          <Grid item xs={5}>
            <div align="left">
              <Typography variant="body2">
                {format(game.startDate, 'ddd MMM D, h:mm a')} @ {game.facilityName}
                {game.cancelled &&
                  <Tooltip title={<Typography color="inherit" variant="body2">Game Cancelled</Typography>}>
                    <img src={cancelledGameIcon} className={classes.icon} height="24" width="auto" alt="Cancelled Game" />
                  </Tooltip>}
              </Typography>
              <Typography variant="body2">
                {game.homeTeamName}(H) v. {game.awayTeamName}(A)
                {isCoach &&
                  <Tooltip title={<Typography color="inherit" variant="body2">Game Coach</Typography>}>
                    <img src={coachCapIcon} className={classes.icon} height="24" width="auto" alt="Coach Cap" />
                  </Tooltip>}
                {isPlateUmpire &&
                  <Tooltip title={<Typography color="inherit" variant="body2">Plate Umpire</Typography>}>
                    <img src={umpMaskIcon} className={classes.icon} height="26" width="auto" alt="Umpire Mask" />
                  </Tooltip>}
                {isBaseUmpire &&
                  <Tooltip title={<Typography color="inherit" variant="body2">Base Umpire</Typography>}>
                    <img src={umpCapIcon} className={classes.icon} height="20" width="auto" alt="Umpire Cap"  />
                  </Tooltip>}
                {isTeamVolunteer &&
                  <Tooltip title={<Typography color="inherit" variant="body2">Team Supporter</Typography>}>
                    <img src={cheerIcon} className={classes.icon} height="20" width="auto" alt="Cheer"  />
                  </Tooltip>}
              </Typography>
              <Typography variant="body2">
                {seasonDivisions[game.divisionId].name}
              </Typography>
              {isAdmin && this.renderUmpsRequested(game)}
            </div>
          </Grid>
          <Grid item xs={7}>
            <div align="left">
              <Grid container spacing={8} alignItems="center">
                <Grid item xs={1}>
                  <Typography variant="body2">
                      P({minPlateLevel}):
                  </Typography>
                </Grid>
                <Grid item xs={10} align="center">
                  {this.renderUmpName(game.umpPlateId, game.umpPlateName, game.umpPlateLevel, game.umpPlateIsYouth, 'umpPlate')}
                </Grid>
                <Grid item xs={1} align="center">
                  {this.renderUmpButton(game.umpPlateId,'umpPlate')}
                </Grid>
              </Grid>
              {umpsRequested >= 2 ? (
                <Grid container spacing={8} alignItems="center">
                  <Grid item xs={1}>
                    <Typography variant="body2">
                        B1({minBase1Level}):
                    </Typography>
                  </Grid>
                  <Grid item xs={10} align="center">
                    {this.renderUmpName(game.umpBase1Id, game.umpBase1Name, game.umpBase1Level, game.umpBase1IsYouth, 'umpBase1')}
                  </Grid>
                  <Grid item xs={1} align="center">
                    {this.renderUmpButton(game.umpBase1Id,'umpBase1')}
                  </Grid>
                </Grid>
                ) : ( '' )
              }
              {umpsRequested >= 3 ? (
                <Grid container spacing={8} alignItems="center">
                  <Grid item xs={1}>
                    <Typography variant="body2">
                        B2({minBase2Level}):
                    </Typography>
                  </Grid>
                  <Grid item xs={10} align="center">
                    {this.renderUmpName(game.umpBase2Id, game.umpBase2Name, game.umpBase2Level, game.umpBase2IsYouth, 'umpBase2')}
                  </Grid>
                  <Grid item xs={1} align="center">
                    {this.renderUmpButton(game.umpBase2Id,'umpBase2')}
                  </Grid>
                </Grid>
                ) : ( '' )
              }
              {umpsRequested >= 4 ? (
                <Grid container spacing={8} alignItems="center">
                  <Grid item xs={1}>
                    <Typography variant="body2">
                    B3({minBase3Level}):
                    </Typography>
                  </Grid>
                  <Grid item xs={10} align="center">
                    {this.renderUmpName(game.umpBase3Id, game.umpBase3Name, game.umpBase3Level, game.umpBase3IsYouth, 'umpBase3')}
                  </Grid>
                  <Grid item xs={1} align="center">
                    {this.renderUmpButton(game.umpBase3Id,'umpBase3')}
                  </Grid>
                </Grid>
                ) : ( '' )
              }
            </div>
          </Grid>
        </Grid>
        <Divider className={classes.divider} />
      </div>
    );
  }
}

GameCard.propTypes = {
  classes: PropTypes.object.isRequired,
  theme: PropTypes.object.isRequired
};

export default withStyles(styles, { withTheme: true })(GameCard);



// WEBPACK FOOTER //
// ./src/components/cards/GameCard.js